import { cva } from "class-variance-authority";
import type { ResponsiveColumns } from "./types";

// ============================================================================
// Default Configuration
// ============================================================================

export const DEFAULT_COLUMNS: ResponsiveColumns = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
  "2xl": 6,
};

export const DEFAULT_GAP = 4;
export const DEFAULT_ESTIMATE_HEIGHT = 200;
export const DEFAULT_OVERSCAN = 3;
export const DEFAULT_LOAD_MORE_THRESHOLD = 300;
export const DEFAULT_LOADING_ITEM_COUNT = 12;
export const DEFAULT_MIN_COLUMN_WIDTH = 280;

// ============================================================================
// CVA Variants
// ============================================================================

/** Grid view container variants */
export const gridViewVariants = cva("w-full flex flex-col", {
  variants: {
    gap: {
      2: "",
      3: "",
      4: "",
      5: "",
      6: "",
      8: "",
    },
  },
  defaultVariants: {
    gap: 4,
  },
});

/** Grid container variants (the actual CSS grid) */
export const gridContainerVariants = cva("grid w-full", {
  variants: {
    gap: {
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
    },
  },
  defaultVariants: {
    gap: 4,
  },
});

/**
 * Grid item variants.
 *
 * Borderless (`docs/borderless-contract.md` §1/§2): `default` is the raised
 * tier, and `outlined` — which was `border-2 border-border` over nothing —
 * becomes a sunken well, since the radius it already carries only reads once
 * the tile has a surface (§4). Hover behaviour per variant is unchanged.
 */
export const gridItemVariants = cva(
  "relative rounded-lg overflow-hidden transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-sunken hover:bg-sunken-strong",
        // `elevated` used the legacy shadcn `bg-card` + Tailwind-default
        // `shadow-md`/`shadow-lg`. Those are heavier than `shadow-overlay`, so
        // a grid tile out-elevated dialogs and popovers. A tile is a raised
        // object like any other, so it shares `default`'s tier; the variant
        // survives only as an alias for its call sites.
        elevated: "bg-sunken hover:bg-sunken-strong",
        outlined: "bg-sunken hover:bg-sunken-strong",
        ghost: "bg-transparent hover:bg-hover",
      },
      interactive: {
        true: "cursor-pointer active:scale-[0.98]",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
    },
  }
);

/** Grid loader variants */
export const gridLoaderVariants = cva(
  "flex items-center justify-center gap-2 py-8 text-muted-foreground",
  {
    variants: {},
    defaultVariants: {},
  }
);

/** Grid empty state variants */
export const gridEmptyVariants = cva(
  "flex flex-col items-center justify-center py-16 text-center text-muted-foreground",
  {
    variants: {
      size: {
        sm: "py-8",
        default: "py-16",
        md: "py-24",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

/** Grid skeleton variants */
export const gridSkeletonVariants = cva("animate-pulse rounded-lg bg-muted", {
  variants: {
    aspectRatio: {
      square: "aspect-square",
      video: "aspect-video",
      portrait: "aspect-[3/4]",
    },
  },
  defaultVariants: {
    aspectRatio: "square",
  },
});

// ============================================================================
// Utility Functions
// ============================================================================

/** Generate responsive grid column classes based on configuration */
export function getResponsiveGridClasses(
  columns: number | ResponsiveColumns
): string {
  if (typeof columns === "number") {
    return `grid-cols-${columns}`;
  }

  const classes: string[] = [];

  if (columns.xs) classes.push(`grid-cols-${columns.xs}`);
  if (columns.sm) classes.push(`sm:grid-cols-${columns.sm}`);
  if (columns.md) classes.push(`md:grid-cols-${columns.md}`);
  if (columns.lg) classes.push(`md:grid-cols-${columns.lg}`);
  if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`);
  if (columns["2xl"]) classes.push(`2xl:grid-cols-${columns["2xl"]}`);

  return classes.join(" ");
}

/** Calculate gap in pixels based on Tailwind spacing */
export function getGapInPixels(gap: number): number {
  // Tailwind gap values: gap-2 = 0.5rem = 8px, gap-4 = 1rem = 16px, etc.
  return gap * 4;
}
